import React from 'react';
import { Header } from '../../common/header.component';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDebouncedState, useLocalStorage } from '@mantine/hooks';
import Toggle from 'rsuite/Toggle';
import { IconRefund } from '../../common/icons/IconRefund';
import { IconDefectCheck } from '../../common/icons/IconDefectCheck';
import { IconAdminAssistance } from '../../common/icons/IconAdminAssistance';
import { IconDelayed } from '../../common/icons/IconDelayed';
import { useConversationStatusesCount } from '../../../api/conversation';
import IconButton from 'rsuite/IconButton';
import Whisper from 'rsuite/Whisper';
import { WhisperSpeaker } from '../../../utils/whisper-speaker';
import Popover from 'rsuite/Popover';
import Dropdown from 'rsuite/Dropdown';
import { DateRangePicker } from 'rsuite';
import moment from 'moment';
import classNames from 'classnames';
import { ManagerSelect, User } from '../../common/user-select';
import { useSortingLocalStorage } from '../../../api/useSortingLocalStorage';

export type OutletContextParams = {
  debouncedInternalId: string;
  linked: boolean;
  start?: Date;
  end?: Date;
  statuses?: string[];
  managerId?: string;
  withoutManager?: boolean;
};

const iconMap: Record<string, React.JSX.Element> = {
  refund: <IconRefund />,
  defectCheck: <IconDefectCheck />,
  adminAssistance: <IconAdminAssistance />,
  delayed: <IconDelayed />,
};

export const ConversationScreen = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const path = pathname.split('/')[2];

  const tabs = [
    {
      title: intl.formatMessage({
        id: 'conversation.status.all',
        defaultMessage: 'Все',
        description: 'Надпись на вкладке',
      }),
      path: 'all',
      name: 'all' as const,
    },
    {
      title: intl.formatMessage({
        id: 'conversation.status.unread',
        defaultMessage: 'Не прочитано',
        description: 'Надпись на вкладке',
      }),
      path: 'unread',
      name: 'unread' as const,
    },
    {
      title: intl.formatMessage({
        id: 'conversation.status.refund',
        defaultMessage: 'Возврат',
        description: 'Надпись на вкладке',
      }),
      path: 'refund',
      name: 'refund' as const,
    },

    {
      title: intl.formatMessage({
        id: 'conversation.status.defectCheck',
        defaultMessage: 'Проверка на брак',
        description: 'Надпись на вкладке',
      }),
      path: 'defectCheck',
      name: 'defectCheck' as const,
    },
    {
      title: intl.formatMessage({
        id: 'conversation.status.delayed',
        defaultMessage: 'Задержка',
        description: 'Надпись на вкладке',
      }),
      path: 'delayed',
      name: 'delayed' as const,
    },
    {
      title: intl.formatMessage({
        id: 'conversation.status.adminAssistance',
        defaultMessage: 'Помощь админа',
        description: 'Надпись на вкладке',
      }),
      path: 'adminAssistance',
      name: 'adminAssistance' as const,
    },
    {
      title: intl.formatMessage({
        id: 'conversation.status.archive',
        defaultMessage: 'Архив',
        description: 'Надпись на вкладке',
      }),
      path: 'archive',
      name: 'archive' as const,
    },
    {
      title: intl.formatMessage({
        id: 'conversation.status.sms',
        defaultMessage: 'СМС',
        description: 'Надпись на вкладке',
      }),
      path: 'sms',
      name: 'sms' as const,
    },
  ];

  const [whisperRootClose, setWhisperRootClose] = React.useState(true);
  const [internalId, setInternalId] = useDebouncedState('', 200);
  const [dates, setDates] = React.useState<[Date, Date]>();
  const [statuses, setStatuses] = React.useState<string[]>([]);
  const [manager, setManager] = useLocalStorage<User | null>({
    key: 'conversation-manager',
    defaultValue: undefined,
  });
  const [withoutManager, setWithoutManager] = useLocalStorage<boolean>({
    key: 'conversation-without-manager',
    defaultValue: false,
  });
  const { data: statusesCount } = useConversationStatusesCount(manager?.id);
  const { sortValues, toggleLinked } = useSortingLocalStorage();

  const context: OutletContextParams = {
    debouncedInternalId: internalId,
    linked: sortValues.linked,
    start: dates?.[0],
    end: dates?.[1],
    statuses,
    managerId: manager?.id,
    withoutManager,
  };

  const renderMenu =
    (): WhisperSpeaker =>
    ({ left, top, className, onClose }, ref) => {
      const handleSelect = async (
        eventKey: string | undefined,
        event: React.SyntheticEvent,
      ) => {
        event.stopPropagation();

        if (eventKey === 'all') {
          navigate('/conversations/all');
          setStatuses([]);
        }
        if (eventKey === 'unread') {
          setStatuses((prevState) => {
            if (prevState.includes('unread')) {
              return prevState.filter((status) => status !== 'unread');
            } else {
              return [...prevState, 'unread'];
            }
          });
        }
        if (eventKey === 'refund') {
          setStatuses((prevState) => {
            if (prevState.includes('refund')) {
              return prevState.filter((status) => status !== 'refund');
            } else {
              return [...prevState, 'refund'];
            }
          });
        }
        if (eventKey === 'defectCheck') {
          setStatuses((prevState) => {
            if (prevState.includes('defectCheck')) {
              return prevState.filter((status) => status !== 'defectCheck');
            } else {
              return [...prevState, 'defectCheck'];
            }
          });
        }
        if (eventKey === 'adminAssistance') {
          setStatuses((prevState) => {
            if (prevState.includes('adminAssistance')) {
              return prevState.filter((status) => status !== 'adminAssistance');
            } else {
              return [...prevState, 'adminAssistance'];
            }
          });
        }

        if (eventKey === 'delayed') {
          setStatuses((prevState) => {
            if (prevState.includes('delayed')) {
              return prevState.filter((status) => status !== 'delayed');
            } else {
              return [...prevState, 'delayed'];
            }
          });
        }
        if (eventKey === 'archive') {
          setStatuses((prevState) => {
            if (prevState.includes('archive')) {
              return prevState.filter((status) => status !== 'archive');
            } else {
              return [...prevState, 'archive'];
            }
          });
        }

        if (eventKey === 'all-week') {
          setDates([
            moment().startOf('week').toDate(),
            moment().endOf('week').toDate(),
          ]);
        }
      };
      return (
        <Popover ref={ref} className={className} style={{ left, top }} full>
          <div className={'d-flex flex-column px-2 py-1 w-100 '}>
            <Dropdown.Menu onSelect={handleSelect}>
              <Dropdown.Item className={'border-bottom'} eventKey={'all'}>
                <div className={'mx-4 px-1 fs-18'}>Все диалоги</div>
              </Dropdown.Item>
              <Dropdown.Item eventKey={'unread'}>
                <div className={'d-flex gap-1'}>
                  <div className={'mx-4 px-1 fs-18 '}>Не прочитано</div>
                  {statuses.includes('unread') && (
                    <div className={'mleft-auto'}>
                      <i className="bi bi-check color-gray-400 fs-24" />
                    </div>
                  )}
                </div>
              </Dropdown.Item>
              <Dropdown.Item eventKey={'refund'}>
                <div className={'d-flex gap-1'}>
                  <div className={'pt-1 fs-18 mx-4 px-1'}>Возврат</div>
                  <div className={'mleft-auto'}> {iconMap['refund']}</div>
                  {statuses.includes('refund') && (
                    <div className={'mleft-auto'}>
                      <i className="bi bi-check color-gray-400 fs-24" />
                    </div>
                  )}{' '}
                </div>
              </Dropdown.Item>
              <Dropdown.Item eventKey={'defectCheck'}>
                <div className={'d-flex gap-1'}>
                  <div className={'pt-1 fs-18 mx-4 px-1'}>Проверка на брак</div>
                  <div className={'mleft-auto'}> {iconMap['defectCheck']}</div>

                  {statuses.includes('defectCheck') && (
                    <div className={'mleft-auto'}>
                      <i className="bi bi-check color-gray-400 fs-24" />
                    </div>
                  )}
                </div>
              </Dropdown.Item>
              <Dropdown.Item eventKey={'adminAssistance'}>
                <div className={'d-flex gap-1'}>
                  <div className={'pt-1 fs-18 mx-4 px-1'}>Помощь админа</div>
                  <div className={'mleft-auto'}>
                    {' '}
                    {iconMap['adminAssistance']}
                  </div>

                  {statuses.includes('adminAssistance') && (
                    <div className={'mleft-auto'}>
                      <i className="bi bi-check color-gray-400 fs-24" />
                    </div>
                  )}
                </div>
              </Dropdown.Item>
              <Dropdown.Item eventKey={'delayed'}>
                <div className={'d-flex gap-1'}>
                  <div className={'pt-1 fs-18  mx-4 px-1'}>Задержка</div>
                  <div className={'mleft-auto'}> {iconMap['delayed']}</div>

                  {statuses.includes('delayed') && (
                    <div className={'mleft-auto'}>
                      <i className="bi bi-check color-gray-400 fs-24" />
                    </div>
                  )}
                </div>
              </Dropdown.Item>
              <Dropdown.Item className={'border-bottom'} eventKey={'archive'}>
                <div className={'d-flex gap-1'}>
                  <div className={'mx-4 px-1 fs-18'}>Архив</div>
                  {statuses.includes('archive') && (
                    <div className={'mleft-auto'}>
                      <i className="bi bi-check color-gray-400 fs-24" />
                    </div>
                  )}
                </div>
              </Dropdown.Item>
              <Dropdown.Item eventKey={'all-week'}>
                <div className={'mx-4 px-1 fs-18'}>Диалоги за неделю</div>
              </Dropdown.Item>
            </Dropdown.Menu>
            <div className={' px-3 fs-14 fw-600 fs-18'}>Диалоги за период:</div>
            <div>
              <DateRangePicker
                onClick={() => setWhisperRootClose(false)}
                onClean={() => setDates(undefined)}
                placeholder={'Укажите даты'}
                format={'dd.MM.yyyy'}
                value={dates}
                isoWeek={true}
                showOneCalendar
                ranges={[]}
                className="w-100 z-3"
                onChange={(value) => {
                  setDates(value || undefined);
                  setWhisperRootClose(true);
                  onClose();
                }}
                onClose={() => setWhisperRootClose(true)}
              />
            </div>
            <div className={' px-3 fs-14 fw-600 fs-18'}>Менеджер:</div>
            <div>
              <ManagerSelect
                roles={['employee', 'admin']}
                placeholder="Выберите менеджера"
                isClearable={true}
                onChange={(user) => {
                  setWithoutManager(!!(user && !user.id));
                  setManager(user);
                }}
                name={'manager'}
                access={'salesManager'}
                value={manager}
              />
            </div>
          </div>
        </Popover>
      );
    };

  return (
    <>
      <Header>
        <div className="d-flex flex-row">
          <h1>
            <FormattedMessage
              defaultMessage="Диалоги"
              id="conversation.label.title"
              description="Заголовок страницы"
            />
          </h1>
          <div className="d-flex mleft-auto pt-3 px-2">
            <Toggle checked={sortValues.linked} onChange={toggleLinked} />
            <div className="ms-2">
              <FormattedMessage
                defaultMessage="Мои клиенты"
                id="switchButton.label.myCustomers"
                description="Надпись на переключателе"
              />
            </div>
          </div>
        </div>
      </Header>
      <div className="d-flex justify-content-between text-center align-items-center pb-4 ">
        <ul className="nav nav-pills d-inline-flex gap-2">
          {tabs.map((item) => (
            <li className="nav-item" key={item.title}>
              <NavLink
                to={item.path}
                className="nav-link text-decoration-none d-flex gap-1"
              >
                <div> {iconMap[item.path] ? iconMap[item.path] : null}</div>
                {item.title}{' '}
                <span className={'color-gray-400'}>
                  {(statusesCount?.[item.name] || 0) > 1999
                    ? '1999+'
                    : statusesCount?.[item.name]}
                </span>
              </NavLink>
            </li>
          ))}
        </ul>
        {path !== 'sms' && (
          <div className={'d-flex gap-2'}>
            <input
              className="form-control"
              type="text"
              placeholder={intl.formatMessage({
                defaultMessage: 'Поиск',
                id: 'input.placeholder.searchByOrder/CustomerNumber',
                description: 'Поле ввода',
              })}
              onChange={(event) => setInternalId(event.currentTarget.value)}
            />
            <Whisper
              placement="leftStart"
              trigger="click"
              speaker={renderMenu()}
              rootClose={whisperRootClose}
            >
              <IconButton
                appearance="subtle"
                icon={
                  <i
                    className={classNames(
                      'bi bi-funnel fs-24 ',
                      statuses.length || dates?.length || manager?.id
                        ? 'color-green'
                        : 'color-gray-400',
                    )}
                  />
                }
              />
            </Whisper>
          </div>
        )}
      </div>
      <Outlet context={context} />
    </>
  );
};
